/**
 * Record a payment: a check, money order or cash posted to us, or a card or
 * eCheck read out over the phone. One payment can cover several titles and
 * gifts, one line each.
 *
 * `form_token` is minted once per mount and kept across retries and error
 * responses, so a double click or a retry meets the server's replay guard.
 * Only a successful save ends the form. Card and bank numbers live in
 * AcceptFields' uncontrolled inputs and never in this component's state.
 */
import { useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api } from '../api/client'
import { useDebounced } from '../lib/useDebounced'
import {
  getMember, getPaymentPlans, getPaymentsConfig, matchPeople, postOfflinePayment, postPhonePayment,
  type DeskPlan, type HeldForReview, type MatchCandidate, type MemberRecord, type PaymentLineBody,
  type PaymentResult, type RecentCharge,
} from './api'
import { AcceptFields, type AcceptHandle } from './AcceptFields'
import { AddressFields, Candidates, RecipientPicker } from './RecipientPicker'
import {
  BLANK_ADDRESS, blankRcpt, newPersonParts, nz, personBody, recipientLineFields, recipientReady, splitName, useRecipientGates,
  type Address, type Recipient,
} from './recipient'
import { HelpLink } from './HelpLink'
import type { GatewayConfig } from './acceptJs'
import {
  authorizationText, balance, centsToInput, dollars, dupGate, isHeldForReview, personKey, recentChargeText, unconfirmedText,
  isManual, linesTotal, METHOD_CARDS, methodDetailBody, methodFields, offerLabel, orderKeyPreview, parseMoney, phoneFailure, saveLabel,
  type FieldSpec, type Method,
} from './payment'
import { fmtMonth } from './member'
import { memberHref, paymentHref } from './links'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const inp = `min-h-[44px] w-full rounded-lg border border-ink-400 bg-ink-800 px-3 text-[15px] text-paper ${focus}`
const lbl = 'flex flex-col gap-1.5 text-[13px] font-semibold text-paper/70'
const panel = 'flex flex-col gap-4 rounded-xl border border-ink-500 bg-ink-700 p-5'
const h2 = 'text-[12px] font-bold uppercase tracking-widest text-paper/60'
const btn = `inline-flex min-h-[44px] items-center justify-center rounded-lg border border-ink-400 px-4 text-[14px] font-bold text-paper hover:bg-ink-600 ${focus}`
const btnPrimary = `inline-flex min-h-[46px] items-center justify-center rounded-lg bg-paper px-5 font-bold text-ink-900 disabled:cursor-not-allowed disabled:opacity-45 ${focus}`
const grid = 'grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-3.5'

interface LineState {
  key: number
  slug: string
  plan_id: string
  price: string
  for: 'payer' | 'gift'
  rcpt: Recipient
}

const today = (): string => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

type Problem =
  | { kind: 'text'; text: string }
  | { kind: 'dup'; candidates: (MatchCandidate & { person?: string })[] }
  | { kind: 'declined'; text: string }
  | { kind: 'recent'; recent: RecentCharge[] }

const detailOf = (e: ApiError): unknown => (e.detail && typeof e.detail === 'object' ? (e.detail as { detail?: unknown }).detail : undefined)

interface Unconfirmed { txn: string | null; invoice: string | null }
class TokenError extends Error {}

function problemFrom(e: unknown, phone: boolean): { problem: Problem; unconfirmed?: Unconfirmed } {
  if (phone && !(e instanceof TokenError)) {
    // A 5xx with our own message is shown as it is; only an unconfirmed charge,
    // a non-JSON 5xx or no answer at all gets the calm "may have been charged".
    const f = e instanceof ApiError ? phoneFailure(e.status, e.detail) : phoneFailure(null, undefined)
    if (f?.kind === 'unconfirmed') return { problem: { kind: 'text', text: '' }, unconfirmed: { txn: f.txn, invoice: f.invoice } }
    if (f?.kind === 'message') return { problem: { kind: 'text', text: f.text } }
  }
  if (!(e instanceof ApiError)) return { problem: { kind: 'text', text: e instanceof TokenError ? e.message : e instanceof Error ? e.message : 'Could not save. Check your connection and try again.' } }
  const d = detailOf(e) as { code?: string; message?: string; candidates?: MatchCandidate[]; recent?: RecentCharge[]; transaction_id?: string | null; invoice_number?: string | null } | string | undefined
  if (typeof d === 'object' && d) {
    if (d.code === 'possible_duplicate') return { problem: { kind: 'dup', candidates: d.candidates ?? [] } }
    if (d.code === 'possible_duplicate_charge') return { problem: { kind: 'recent', recent: d.recent ?? [] } }
    if (d.code === 'declined') return { problem: { kind: 'declined', text: d.message ?? 'The card was declined.' } }
    if (d.code === 'unconfirmed') return { problem: { kind: 'text', text: '' }, unconfirmed: { txn: d.transaction_id ?? null, invoice: d.invoice_number ?? null } }
  }
  if (typeof d === 'string') return { problem: { kind: 'text', text: d } }
  if (Array.isArray(d)) return { problem: { kind: 'text', text: (d[0] as { msg?: string } | undefined)?.msg ?? 'Check the form and try again.' } }
  return { problem: { kind: 'text', text: e.status === 429 ? 'Too many tries. Wait a few minutes and try again.' : 'Could not save. Try again.' } }
}

export function PaymentPage() {
  const rawId = useParams().id ?? ''
  const isNewPayer = rawId === 'new'
  const memberId = Number(rawId)
  const [search] = useSearchParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const batchParam = search.get('batch')
  const batchId = batchParam && Number.isFinite(Number(batchParam)) ? Number(batchParam) : null

  const [formToken] = useState(() => crypto.randomUUID())
  const acceptRef = useRef<AcceptHandle>(null)

  const member = useQuery<MemberRecord>({ queryKey: ['desk-member', memberId], queryFn: () => getMember(memberId), enabled: !isNewPayer && Number.isFinite(memberId) })
  const plansQ = useQuery({ queryKey: ['desk-pay-plans'], queryFn: getPaymentPlans })
  const cfgQ = useQuery({ queryKey: ['desk-pay-config'], queryFn: getPaymentsConfig })
  const plans: DeskPlan[] = useMemo(() => plansQ.data?.plans ?? [], [plansQ.data])
  const titles = useMemo(() => {
    const seen = new Map<string, string>()
    for (const p of plans) if (!seen.has(p.publication_slug)) seen.set(p.publication_slug, p.publication_name)
    return [...seen].map(([slug, name]) => ({ slug, name }))
  }, [plans])

  const [method, setMethod] = useState<Method>('check')
  const [vals, setVals] = useState<Record<string, string>>({ received_date: today(), account_type: 'checking' })
  const setVal = (k: string, v: string) => setVals((s) => ({ ...s, [k]: v }))
  const [keepCard, setKeepCard] = useState(false)
  const [stopAr, setStopAr] = useState(false)
  const [agreed, setAgreed] = useState(false)
  const [confirmedKey, setConfirmedKey] = useState<string | null>(null)

  const [payerName, setPayerName] = useState('')
  const [payerAddr, setPayerAddr] = useState<Address>({ ...BLANK_ADDRESS })
  const [payerPhone, setPayerPhone] = useState('')
  const [payerDismissedKey, setPayerDismissedKey] = useState<string | null>(null)
  const inFlight = useRef(false)

  const firstSlug = search.get('title') ?? ''
  const nextKey = useRef(1)
  const [lines, setLines] = useState<LineState[]>([{ key: 0, slug: firstSlug, plan_id: '', price: '', for: 'payer', rcpt: blankRcpt() }])

  const [problem, setProblem] = useState<Problem | null>(null)
  const [unconfirmed, setUnconfirmed] = useState<Unconfirmed | null>(null)
  const [result, setResult] = useState<PaymentResult | null>(null)
  const [held, setHeld] = useState<HeldForReview | null>(null)

  const payerParts = splitName(payerName)
  const dupKey = useDebounced({ ...payerParts, a: payerAddr.address_1, z: payerAddr.zipcode, e: payerAddr.email, p: payerPhone }, 400)
  const payerMatch = useQuery({
    queryKey: ['desk-match-payer', dupKey],
    queryFn: () => matchPeople({ first_name: dupKey.first_name, last_name: dupKey.last_name, address_1: dupKey.a, zipcode: dupKey.z, email: dupKey.e, phone: dupKey.p }),
    enabled: isNewPayer && (dupKey.last_name.length >= 2 || dupKey.e.length > 3),
  })
  const payerCandidates = payerMatch.data?.candidates ?? []
  const payerSearching = isNewPayer && (payerParts.last_name.length >= 2 || payerAddr.email.length > 3)
  const payerKey = personKey(payerName, payerAddr.address_1, payerAddr.zipcode, payerAddr.email, payerPhone)
  const payerGate = dupGate({
    searching: payerSearching,
    settled: payerMatch.data !== undefined && !payerMatch.isFetching && JSON.stringify(dupKey) === JSON.stringify({ ...payerParts, a: payerAddr.address_1, z: payerAddr.zipcode, e: payerAddr.email, p: payerPhone }),
    candidates: payerCandidates.length, dismissedKey: payerDismissedKey, key: payerKey,
  })

  const gw = useQuery({
    queryKey: ['desk-pay-gateway'],
    queryFn: () => api.get<GatewayConfig>('/api/public/gateway-config'),
    enabled: !isManual(method),
    staleTime: Infinity,
  })

  const cfg = cfgQ.data
  const phoneOn = !!cfg?.phone_payments_enabled
  const cards = METHOD_CARDS.filter((m) => (m.method === 'card' ? phoneOn : m.method === 'bank' ? phoneOn && !!cfg?.echeck_enabled : true))
  const payerRecord = member.data ?? null
  const payerLabel = payerRecord?.name ?? (payerName.trim() || 'the payer')

  const planFor = (l: LineState): DeskPlan | undefined => plans.find((p) => p.publication_slug === l.slug && p.plan_id === l.plan_id)
  const patchLine = (key: number, patch: Partial<LineState>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)))
  const pickTitle = (key: number, slug: string) => {
    const first = plans.find((p) => p.publication_slug === slug)
    patchLine(key, { slug, plan_id: first?.plan_id ?? '', price: first ? centsToInput(first.amount_cents) : '' })
  }
  const pickPlan = (key: number, slug: string, planId: string) => {
    const p = plans.find((x) => x.publication_slug === slug && x.plan_id === planId)
    patchLine(key, { plan_id: planId, price: p ? centsToInput(p.amount_cents) : '' })
  }
  // A ?title= preselect (or a title just picked) gets its first offer and price by default.
  const resolved = lines.map((raw) => {
    const scOwned = raw.for === 'payer' && !!payerRecord?.titles.find((c) => c.slug === raw.slug)?.sc_owned
    const l = scOwned ? { ...raw, slug: '', plan_id: '', price: '' } : raw
    const first = l.slug && !l.plan_id ? plans.find((x) => x.publication_slug === l.slug) : undefined
    return first ? { ...l, plan_id: first.plan_id, price: l.price || centsToInput(first.amount_cents) } : l
  })

  const priced = resolved.map((l) => ({ ...l, price_cents: parseMoney(l.price) }))
  const total = linesTotal(priced)
  const amountCents = parseMoney(vals.amount ?? '')
  const bal = balance(method, amountCents, priced)
  const manual = isManual(method)

  const rcptGates = useRecipientGates(resolved.map((l) => (l.for === 'gift' ? l.rcpt : null)), isNewPayer ? null : memberId)
  const rcptOk = (l: LineState, i: number): boolean => l.for === 'payer' || recipientReady(l.rcpt, rcptGates[i])
  const linesOk = priced.every((l, i) => l.slug && l.plan_id && l.price_cents > 0 && rcptOk(l, i))
  const payerOk = isNewPayer
    ? (payerName.trim().length > 0 || payerAddr.company.trim().length > 0) && payerAddr.address_1.trim() !== ''
      && (payerAddr.country !== 'OTHER' || payerAddr.country_other.trim() !== '')
    : !!payerRecord
  const needsDupAnswer = payerGate.blocked
  const fieldsOk = methodFields(method).every((f) => !f.required || f.key === 'amount' || (vals[f.key] ?? '').trim() !== '')
  const bankOk = method !== 'bank' || agreed
  const canSave = bal.ok && linesOk && payerOk && !needsDupAnswer && fieldsOk && bankOk && !unconfirmed && !held && !result

  const arCards = (payerRecord?.titles ?? []).filter((t) => resolved.some((l) => l.for === 'payer' && l.slug === t.slug) && t.auto_renew)

  const linesBody = (): PaymentLineBody[] => priced.map((l) => {
    const base = { publication_slug: l.slug, plan_id: l.plan_id, price_cents: l.price_cents, for: l.for }
    if (l.for === 'payer') return base
    return { ...base, ...recipientLineFields(l.rcpt) }
  })

  const peopleKey = personKey(
    payerKey, ...resolved.flatMap((l) => (l.for === 'gift' ? newPersonParts(l.rcpt) : [])),
  )

  const save = useMutation({
    // chargeAgain: staff saw a recent charge for this payer and mean to charge again.
    mutationFn: async (opts?: { chargeAgain?: boolean }): Promise<PaymentResult | HeldForReview> => {
      const common = { form_token: formToken, lines: linesBody(), confirm_not_duplicate: confirmedKey === peopleKey }
      if (manual) {
        return postOfflinePayment({
          ...common, method,
          ...(isNewPayer ? { new_payer: personBody(payerParts, payerAddr, { phone: payerPhone }) } : { payer_subscriber_id: memberId }),
          reference: (vals.reference ?? '').trim(), reference_date: nz(vals.reference_date ?? ''),
          received_date: vals.received_date, amount_cents: amountCents, ...methodDetailBody(method, vals),
          stop_auto_renew: stopAr && arCards.length > 0, ...(batchId ? { batch_id: batchId } : {}),
        })
      }
      const nonce = await acceptRef.current!.tokenize({
        zip: vals.bill_zip, fullName: vals.name_on_account, accountType: vals.account_type,
      }).catch((e: unknown) => { throw new TokenError(e instanceof Error ? e.message : 'The card form could not read those details.') })
      return postPhonePayment({
        ...common, instrument: method,
        // A new payer is created only once the card is approved.
        ...(isNewPayer ? { new_payer: personBody(payerParts, payerAddr, { phone: payerPhone }) } : { payer_subscriber_id: memberId }),
        opaque_data: { dataDescriptor: nonce.descriptor, dataValue: nonce.value },
        bill_zip: (vals.bill_zip ?? '').trim(), keep_card_for_auto_renew: keepCard,
        ...(opts?.chargeAgain ? { confirm_charge_again: true } : {}),
        ...(batchId ? { batch_id: batchId } : {}),
        // In a Mail Order Batch the member's signature on the form authorizes the debit.
        ...(method === 'bank' ? { account_type: vals.account_type, ...(batchId
          ? { signed_form_authorization: true }
          : { authorization_text: authorizationText(vals.name_on_account ?? '', total, vals.account_type) }) } : {}),
      })
    },
    onSettled: () => { inFlight.current = false },
    onSuccess: (r) => {
      setProblem(null)
      if (isHeldForReview(r)) { setHeld(r); return }
      setResult(r)
      void qc.invalidateQueries({ queryKey: ['desk-member'] })
      void qc.invalidateQueries({ queryKey: ['desk-activity'] })
    },
    onError: (e) => {
      const p = problemFrom(e, !manual)
      if (p.unconfirmed) setUnconfirmed(p.unconfirmed)
      setProblem(p.unconfirmed ? null : p.problem)
    },
  })

  if (!isNewPayer && member.isError) {
    return <main className="p-6" role="alert">We could not find that member. <Link className="underline" to="/desk/find">Find a member</Link></main>
  }

  if (result) {
    return (
      <main className="flex min-w-0 flex-1 flex-col gap-5 px-4 pb-16 pt-8 md:px-12">
        <h1 className="font-serif text-[32px]">Payment recorded</h1>
        <section className={panel} role="status">
          <p>{dollars(result.total_cents)} recorded{result.transaction_id ? ` (transaction ${result.transaction_id})` : ''}.</p>
          <ul className="flex flex-col gap-2">
            {result.orders.map((o) => (
              <li key={o.order_key} className="flex flex-col">
                <span className="font-mono text-[13px] [overflow-wrap:anywhere]">{o.order_key}</span>
                <span className="text-[13px] text-paper/70">
                  {titles.find((t) => t.slug === o.publication_slug)?.name ?? o.publication_slug}
                  {o.new_end ? `: now runs to ${fmtMonth(o.new_end)}` : ''}
                </span>
              </li>
            ))}
          </ul>
          {result.card_kept && <p className="text-[13px] text-paper/70">The card was kept for auto-renew.</p>}
          <div className="flex flex-wrap gap-3">
            <Link className={btnPrimary} to={memberHref(result.payer_subscriber_id)}>Back to the member record</Link>
            {batchId && <Link className={btn} to={`/desk/batch/${batchId}`}>Back to the batch</Link>}
          </div>
        </section>
      </main>
    )
  }

  const fieldInput = (f: FieldSpec) => {
    const common = { className: f.kind === 'money' ? `${inp} font-mono` : inp, value: vals[f.key] ?? '' }
    if (f.kind === 'select') {
      return <select {...common} onChange={(e) => setVal(f.key, e.target.value)}>{f.options!.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
    }
    return <input {...common} type={f.kind === 'date' ? 'date' : 'text'} inputMode={f.kind === 'money' ? 'decimal' : undefined}
      placeholder={f.placeholder} onChange={(e) => setVal(f.key, e.target.value)} />
  }

  const slugsForKeys = resolved.map((l) => l.slug || '[title]')
  const acct = payerRecord?.account_number ?? ''
  const effects = priced.map((l) => {
    const t = titles.find((x) => x.slug === l.slug)?.name ?? 'Choose a title'
    const p = planFor(l)
    const who = l.for === 'payer' ? payerLabel : l.rcpt.mode === 'existing' ? (l.rcpt.label ?? 'the recipient') : (l.rcpt.name.trim() || 'the recipient')
    return `${t}: ${l.for === 'gift' ? `gift for ${who}, ${payerLabel} pays` : who}${p ? `, ${p.term_months} months added` : ''}.`
  })

  return (
    <main className="flex min-w-0 flex-1 flex-col gap-6 px-4 pb-16 pt-8 md:px-12">
      <Link to={isNewPayer ? '/desk/find' : memberHref(memberId)} className="text-[13px] underline-offset-2 hover:underline">
        ← {isNewPayer ? 'Find a member' : payerRecord ? `${payerRecord.name}${payerRecord.account_number ? ` #${payerRecord.account_number}` : ''}` : 'Back'}
      </Link>
      <header className="flex flex-col gap-1.5">
        <h1 className="font-serif text-[32px]">Record a payment</h1>
        <p className="max-w-[700px] text-paper/70">For payments taken by staff: mail, phone or in person. One payment can cover several titles and gifts. Add a line for each title it pays for.</p>
        <HelpLink page="pay" />
      </header>

      <div className="flex flex-wrap items-start gap-6">
        <div className="flex min-w-0 flex-[999_1_560px] flex-col gap-5">
          {isNewPayer && (
            <section className={panel} aria-labelledby="payer-h">
              <h2 id="payer-h" className={h2}>Who is paying</h2>
              <div className={grid}>
                <label className={`${lbl} col-span-2`}>Name<input className={inp} value={payerName} onChange={(e) => setPayerName(e.target.value)} /></label>
                <label className={lbl}>Phone (optional)<input className={inp} value={payerPhone} onChange={(e) => setPayerPhone(e.target.value)} /></label>
              </div>
              <AddressFields a={payerAddr} set={setPayerAddr} />
              {(payerGate.asking || payerGate.blocked) && payerSearching && !payerGate.asking && (
                <span className="text-[13px] text-paper/70">Checking for people we already have…</span>
              )}
              {payerGate.asking && (
                <Candidates list={payerCandidates}
                  onUse={(c) => navigate(paymentHref(c.subscriber_id, Object.fromEntries(search.entries())))}
                  onDifferent={() => setPayerDismissedKey(payerKey)} />
              )}
            </section>
          )}

          <section className={panel} aria-labelledby="how-h">
            <h2 id="how-h" className={h2}>How they paid</h2>
            <div role="radiogroup" aria-labelledby="how-h" className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-2.5">
              {cards.map((m) => (
                <button key={m.method} type="button" role="radio" aria-checked={method === m.method}
                  onClick={() => { setMethod(m.method); setProblem(null) }}
                  className={`flex min-h-[64px] flex-col items-start gap-1 rounded-xl border px-3.5 py-3 text-left ${focus} ${method === m.method ? 'border-amber-glow bg-amber-wash' : 'border-ink-400'}`}>
                  <span className="text-[14px] font-bold">{m.label}</span>
                  <span className="text-[12px] text-paper/60">{m.sub}</span>
                </button>
              ))}
            </div>
            <div className={grid}>
              {methodFields(method).map((f) => (
                <label key={f.key} className={lbl}>{f.label}{!f.required && f.key !== 'reference' ? ' (optional)' : ''}{fieldInput(f)}</label>
              ))}
            </div>
            {!manual && (
              <>
                <p className="text-[13px] leading-relaxed text-paper/70">
                  {method === 'card'
                    ? 'Card details go straight to Authorize.Net. AAMCirc never sees or stores the number. The card is charged the total of the lines below.'
                    : 'Bank details go straight to Authorize.Net. eChecks take a few days to settle, and the membership starts now.'}
                </p>
                {gw.data && <AcceptFields ref={acceptRef} instrument={method === 'card' ? 'card' : 'bank'} config={gw.data} disabled={save.isPending || !!unconfirmed || !!held} />}
                {gw.isError && <p role="alert" className="text-[13px] text-radar-red">The card form could not be loaded.</p>}
                {method === 'card' && (
                  <label className="inline-flex min-h-[32px] items-center gap-2 text-paper/70">
                    <input type="checkbox" checked={keepCard} onChange={(e) => setKeepCard(e.target.checked)} />
                    Keep this card and turn on auto-renew for these titles ({batchId ? 'the member asked for it on the form' : 'the member agreed on the phone'})
                  </label>
                )}
                {method === 'bank' && batchId && (
                  <label className="inline-flex items-center gap-2 rounded-lg bg-ink-600 p-3.5"><input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />The member signed the debit authorization on the form</label>
                )}
                {method === 'bank' && !batchId && (
                  <div className="flex flex-col gap-2 rounded-lg bg-ink-600 p-3.5">
                    <p className="text-[13px] leading-relaxed">Read this to the member: &ldquo;You authorize a one-time debit of {dollars(total)} from your bank account for these memberships.&rdquo;</p>
                    <label className="inline-flex items-center gap-2"><input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />I read this to the member and they agreed</label>
                  </div>
                )}
              </>
            )}
          </section>

          <section className={panel} aria-labelledby="lines-h">
            <h2 id="lines-h" className={h2}>What it pays for</h2>
            {resolved.map((l, i) => {
              const offers = plans.filter((p) => p.publication_slug === l.slug)
              return (
                <div key={l.key} className="flex flex-col gap-3.5 rounded-xl border border-ink-500 p-4">
                  <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3.5">
                    <label className={lbl}>Title
                      <select className={inp} value={l.slug} onChange={(e) => pickTitle(l.key, e.target.value)}>
                        <option value="">Choose a title</option>
                        {titles.map((t) => {
                          const sc = l.for === 'payer' && payerRecord?.titles.find((c) => c.slug === t.slug)?.sc_owned
                          return <option key={t.slug} value={t.slug} disabled={!!sc}>{t.name}{sc ? ' (billed in SimpleCirc)' : ''}</option>
                        })}
                      </select>
                    </label>
                    <label className={lbl}>Offer
                      <select className={inp} value={l.plan_id} onChange={(e) => pickPlan(l.key, l.slug, e.target.value)} disabled={!l.slug}>
                        {!l.plan_id && <option value="">Choose an offer</option>}
                        {offers.map((p) => <option key={p.plan_id} value={p.plan_id}>{offerLabel(p.term_months)}</option>)}
                      </select>
                    </label>
                    <label className={lbl}>Price
                      <input className={`${inp} font-mono`} inputMode="decimal" value={l.price} onChange={(e) => patchLine(l.key, { price: e.target.value })} />
                    </label>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-[13px] font-semibold text-paper/70">For</span>
                    <span role="radiogroup" aria-label="Who the membership is for" className="inline-flex flex-wrap rounded-lg bg-ink-600 p-[3px]">
                      {([['payer', `${payerLabel} (the payer)`], ['gift', 'Someone else (a gift)']] as const).map(([v, label]) => (
                        <button key={v} type="button" role="radio" aria-checked={l.for === v} onClick={() => patchLine(l.key, { for: v })}
                          className={`min-h-[36px] rounded-md px-3 text-[13px] font-semibold ${focus} ${l.for === v ? 'bg-ink-800 text-paper ring-1 ring-ink-400' : 'text-paper/70'}`}>{label}</button>
                      ))}
                    </span>
                    {lines.length > 1 && <button type="button" className={`${btn} ml-auto`} onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}>Remove line {i + 1}</button>}
                  </div>
                  {l.for === 'gift' && <RecipientPicker r={l.rcpt} set={(rcpt) => patchLine(l.key, { rcpt })} gate={rcptGates[i]} />}
                </div>
              )
            })}
            <button type="button" className={`${btn} self-start`}
              onClick={() => setLines((ls) => [...ls, { key: nextKey.current++, slug: '', plan_id: '', price: '', for: 'payer', rcpt: blankRcpt() }])}>
              + Add a title or a gift
            </button>
          </section>

          <div role="status" className={`flex items-center gap-2.5 rounded-lg px-3.5 py-3 font-semibold ${bal.ok ? 'bg-radar-green/10 text-radar-green' : 'bg-amber-wash text-amber-glow'}`}>
            <span>{bal.text}</span>
          </div>

          <section className={panel} aria-labelledby="ar-h">
            <h2 id="ar-h" className={h2}>Card auto-renew</h2>
            {manual ? (
              arCards.length === 0 ? (
                <p className="leading-relaxed text-paper/70">The member has no card auto-renew on these titles, so there is nothing to stop. If they did, this payment would extend the membership and card billing would stay on unless you tick &ldquo;Also stop card auto-renew&rdquo;.</p>
              ) : (
                <label className="flex items-start gap-2 text-paper/70">
                  <input type="checkbox" className="mt-1" checked={stopAr} onChange={(e) => setStopAr(e.target.checked)} />
                  <span>Also stop card auto-renew on {arCards.map((c) => c.name).join(', ')}. Without this, card billing stays on and the member could be charged again.</span>
                </label>
              )
            ) : (
              <p className="leading-relaxed text-paper/70">Leave the box above unticked for a one-off payment. Tick it only if the member asked to be billed automatically from now on.</p>
            )}
          </section>

          {problem?.kind === 'text' && problem.text && <p role="alert" className="text-radar-red">{problem.text}</p>}
          {problem?.kind === 'declined' && (
            <p role="alert" className="text-radar-red">{problem.text} Nothing was charged. Ask for another card, or take a check instead.</p>
          )}
          {problem?.kind === 'dup' && (
            <div role="alert" className="flex flex-col gap-3 rounded-xl border border-amber-glow/40 bg-amber-wash p-4">
              <strong>This may be someone we already have</strong>
              {problem.candidates.map((c, i) => (
                <span key={`${c.subscriber_id}-${i}`} className="text-[13px]">{c.name} {c.account_number ? `#${c.account_number}` : ''} {c.zipcode ?? ''}</span>
              ))}
              <button type="button" className={`${btn} self-start`} onClick={() => { setConfirmedKey(peopleKey); setProblem(null) }}>
                These are all different people. Save again.
              </button>
            </div>
          )}
          {problem?.kind === 'recent' && (
            <div role="alert" className="flex flex-col gap-3 rounded-xl border border-amber-glow/40 bg-amber-wash p-4">
              <strong>{payerLabel} was charged by phone a few minutes ago</strong>
              <ul className="flex flex-col gap-1 text-[13px]">
                {problem.recent.map((c, i) => <li key={`${c.transaction_id ?? 'none'}-${i}`}>{recentChargeText(c)}</li>)}
              </ul>
              <span className="text-[13px] leading-relaxed">If the page was reloaded or this payment was started in another tab, it has probably gone through already. Check the member record before charging again.</span>
              <button type="button" className={`${btn} self-start`} disabled={save.isPending}
                onClick={() => { if (inFlight.current) return; inFlight.current = true; setProblem(null); save.mutate({ chargeAgain: true }) }}>
                Charge again anyway
              </button>
            </div>
          )}
          {held && (
            <p role="status" className="rounded-xl border border-amber-glow/40 bg-amber-wash p-4 leading-relaxed">
              {held.message}{' '}
              <span className="text-paper/70">Transaction <strong className="font-mono">{held.transaction_id}</strong>.</span>
            </p>
          )}
          {unconfirmed && (
            <p role="alert" className="rounded-xl border border-radar-red/50 p-4 leading-relaxed">
              {unconfirmedText(method === 'bank' ? 'bank' : 'card', unconfirmed.txn, unconfirmed.invoice)}{' '}
              {(unconfirmed.txn ?? unconfirmed.invoice) && <strong className="font-mono">{unconfirmed.txn ?? unconfirmed.invoice}</strong>}
            </p>
          )}

          <div className="flex flex-wrap gap-2.5">
            <button type="button" className={btnPrimary} disabled={!canSave || save.isPending} onClick={() => { if (inFlight.current) return; inFlight.current = true; save.mutate(undefined) }}>
              {save.isPending ? 'Saving…' : saveLabel(method, vals.reference ?? '', total)}
            </button>
            <Link className={btn} to={isNewPayer ? '/desk/find' : memberHref(memberId)}>Cancel</Link>
          </div>
        </div>

        <aside className={`${panel} min-w-0 flex-[1_1_300px]`} aria-labelledby="sum-h">
          <h2 id="sum-h" className={h2}>When you save</h2>
          <ul className="flex list-disc flex-col gap-2.5 pl-[18px] leading-relaxed text-paper/70">
            {effects.map((x, i) => <li key={i}>{x}</li>)}
            <li>{manual ? `A thank-you email goes to ${payerLabel} listing the titles.` : 'The receipt is emailed as soon as the charge is approved.'}</li>
            <li>The payment shows on {payerLabel}&rsquo;s record. Each gift also shows on the recipient&rsquo;s record.</li>
          </ul>
          <div className="flex flex-col gap-1.5 border-t border-ink-500 pt-3.5">
            <span className="text-[12px] text-paper/60">Order references</span>
            {orderKeyPreview(method, acct, vals.reference ?? '', slugsForKeys).map((k, i) => <span key={i} className="font-mono text-[12px] [overflow-wrap:anywhere]">{k}</span>)}
          </div>
          <p className="text-[12px] leading-relaxed text-paper/60">
            {manual ? 'Entering the same payment twice is refused. If a check bounces, void it from the member record.' : 'A decline leaves nothing behind. Ask for another card or a check instead.'}
          </p>
        </aside>
      </div>
    </main>
  )
}
